import { useState, useEffect } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { 
  UserPlus, 
  Mail, 
  Clock, 
  Check, 
  X,
  Copy,
  Trash2,
  Send,
  RefreshCw
} from 'lucide-react'
import { getFirebaseDb, getCallableFunction } from '@/lib/firebase'
import { useAuth } from '@/contexts/AuthContext'
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  deleteDoc, 
  doc, 
  serverTimestamp,
  Timestamp,
  orderBy
} from 'firebase/firestore'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface Invite {
  id: string
  email: string
  role: string
  status: 'pending' | 'accepted' | 'expired'
  token: string
  invitedBy: string
  invitedByEmail?: string
  createdAt: Date
  expiresAt: Date
}

interface InviteMembersProps {
  organizationId: string
  organizationName: string
}

export function InviteMembers({ organizationId, organizationName }: InviteMembersProps) {
  const { user } = useAuth()
  const [invites, setInvites] = useState<Invite[]>([])
  const [loading, setLoading] = useState(true)
  const [isInviteOpen, setIsInviteOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('member')
  const [sending, setSending] = useState(false)
  const [resendingInviteId, setResendingInviteId] = useState<string | null>(null)
  const [copiedToken, setCopiedToken] = useState<string | null>(null)
  const [deleteInviteId, setDeleteInviteId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    fetchInvites()
  }, [organizationId])

  const fetchInvites = async () => {
    if (!organizationId) return
    
    try {
      const db = getFirebaseDb()
      const invitesRef = collection(db, 'organizations', organizationId, 'invites')
      const invitesQuery = query(invitesRef, orderBy('createdAt', 'desc'))
      const snapshot = await getDocs(invitesQuery)
      
      const invitesData = snapshot.docs.map(docSnap => {
        const data = docSnap.data()
        const expiresAt = data.expiresAt?.toDate() || new Date()
        const now = new Date()
        
        // Check if expired
        let status = data.status
        if (status === 'pending' && expiresAt < now) {
          status = 'expired'
        }
        
        return {
          id: docSnap.id,
          email: data.email,
          role: data.role,
          status,
          token: data.token,
          invitedBy: data.invitedBy,
          invitedByEmail: data.invitedByEmail,
          createdAt: data.createdAt?.toDate() || new Date(),
          expiresAt
        }
      }) as Invite[]
      
      setInvites(invitesData)
    } catch (error) {
      console.error('Error fetching invites:', error)
    } finally {
      setLoading(false)
    }
  }

  const generateToken = () => {
    const array = new Uint8Array(32)
    crypto.getRandomValues(array)
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('')
  }

  const handleSendInvite = async () => {
    if (!organizationId || !inviteEmail.trim() || !user) return
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(inviteEmail.trim())) {
      setError('Please enter a valid email address')
      return
    }
    
    setSending(true)
    setError(null)
    
    try {
      const db = getFirebaseDb()
      const invitesRef = collection(db, 'organizations', organizationId, 'invites')
      
      // Check if invite already exists for this email
      const existingQuery = query(invitesRef, where('email', '==', inviteEmail.trim().toLowerCase()))
      const existingSnapshot = await getDocs(existingQuery)
      
      if (!existingSnapshot.empty) {
        const existingInvite = existingSnapshot.docs[0].data()
        if (existingInvite.status === 'pending') {
          setError('An invite has already been sent to this email')
          setSending(false)
          return
        }
      }
      
      const token = generateToken()
      const expiresAt = new Date()
      expiresAt.setDate(expiresAt.getDate() + 7) // 7 day expiry
      
      const inviteData = {
        email: inviteEmail.trim().toLowerCase(),
        role: inviteRole,
        status: 'pending',
        token,
        invitedBy: user.uid,
        invitedByEmail: user.email,
        createdAt: serverTimestamp(),
        expiresAt: Timestamp.fromDate(expiresAt),
        organizationId,
        organizationName
      }
      
      const docRef = await addDoc(invitesRef, inviteData)
      
      // Call Cloud Function to send email
      try {
        const sendInviteEmail = getCallableFunction<{
          inviteId: string
          organizationId: string
          email: string
          role: string
          inviteUrl: string
          organizationName: string
        }, { success: boolean; message: string }>('sendInviteEmail')
        
        const inviteUrl = `${window.location.origin}/invite/${token}`
        await sendInviteEmail({
          inviteId: docRef.id,
          organizationId,
          email: inviteEmail.trim().toLowerCase(),
          role: inviteRole,
          inviteUrl,
          organizationName
        })
        
        setSuccess(`Invite email sent to ${inviteEmail}`)
      } catch (emailError) {
        console.warn('Email send failed, invite still created:', emailError)
        setSuccess(`Invite created for ${inviteEmail} (email delivery pending)`)
      }
      
      setInviteEmail('')
      setInviteRole('member')
      setIsInviteOpen(false)
      fetchInvites()
    } catch (error) {
      console.error('Error sending invite:', error)
      setError('Failed to send invite')
    } finally {
      setSending(false)
    }
  }

  const handleDeleteInvite = async () => {
    if (!organizationId || !deleteInviteId) return
    
    try {
      const db = getFirebaseDb()
      await deleteDoc(doc(db, 'organizations', organizationId, 'invites', deleteInviteId))
      setInvites(invites.filter(i => i.id !== deleteInviteId))
      setDeleteInviteId(null)
    } catch (error) {
      console.error('Error deleting invite:', error)
    }
  }

  const handleResendInvite = async (invite: Invite) => {
    if (!organizationId || !user) return
    
    setResendingInviteId(invite.id)
    setError(null)
    
    try {
      // Call Cloud Function to resend email
      const sendInviteEmail = getCallableFunction<{
        inviteId: string
        organizationId: string
        email: string
        role: string
        inviteUrl: string
        organizationName: string
      }, { success: boolean; message: string }>('sendInviteEmail')
      
      const inviteUrl = `${window.location.origin}/invite/${invite.token}`
      await sendInviteEmail({
        inviteId: invite.id,
        organizationId,
        email: invite.email,
        role: invite.role,
        inviteUrl,
        organizationName
      })
      
      setSuccess(`Invite resent to ${invite.email}`)
    } catch (error) {
      console.error('Error resending invite:', error)
      setError('Failed to resend invite')
    } finally {
      setResendingInviteId(null)
    }
  }

  const copyInviteLink = async (token: string) => {
    const inviteUrl = `${window.location.origin}/invite/${token}`
    await navigator.clipboard.writeText(inviteUrl)
    setCopiedToken(token)
    setTimeout(() => setCopiedToken(null), 2000)
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="text-amber-600 border-amber-600"><Clock className="h-3 w-3 mr-1" />Pending</Badge>
      case 'accepted':
        return <Badge variant="default" className="bg-green-600"><Check className="h-3 w-3 mr-1" />Accepted</Badge>
      case 'expired':
        return <Badge variant="secondary"><X className="h-3 w-3 mr-1" />Expired</Badge>
      default:
        return null
    }
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Invite Members</h3>
          <p className="text-sm text-muted-foreground">
            Send invitations to add new members to this organization
          </p>
        </div>
        
        <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
          <DialogTrigger asChild>
            <Button>
              <UserPlus className="h-4 w-4 mr-2" />
              Invite Member
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite a New Member</DialogTitle>
              <DialogDescription>
                Send an invitation email to add someone to {organizationName}
              </DialogDescription>
            </DialogHeader>
            
            {error && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive">
                {error}
              </div>
            )}
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@company.com"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select value={inviteRole} onValueChange={setInviteRole}>
                  <SelectTrigger id="role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Admins can manage organization settings and invite other members
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsInviteOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSendInvite} disabled={sending || !inviteEmail.trim()}>
                {sending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Invite
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {success && (
        <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-md text-sm text-green-600 dark:text-green-400">
          {success}
        </div>
      )}

      {/* Pending Invites List */}
      {invites.length === 0 ? (
        <Card>
          <CardContent className="py-8">
            <div className="text-center">
              <Mail className="mx-auto h-10 w-10 text-muted-foreground" />
              <h4 className="mt-3 font-medium">No Pending Invites</h4>
              <p className="mt-1 text-sm text-muted-foreground">
                Invite team members to collaborate on this organization
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {invites.map((invite) => (
            <Card key={invite.id}>
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                      <Mail className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{invite.email}</span>
                        {getStatusBadge(invite.status)}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Invited as {invite.role} • Expires {formatDate(invite.expiresAt)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {invite.status === 'pending' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyInviteLink(invite.token)}
                      >
                        {copiedToken === invite.token ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                    {invite.status === 'pending' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResendInvite(invite)}
                        disabled={resendingInviteId === invite.id}
                      >
                        <RefreshCw className={`h-4 w-4 ${resendingInviteId === invite.id ? 'animate-spin' : ''}`} />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteInviteId(invite.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteInviteId} onOpenChange={() => setDeleteInviteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invite?</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel the invitation. The invite link will no longer work.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteInvite} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
